/**
 * Compass Explorer — finding-severity attribution.
 *
 * Walks every finding node in the graph (policy violations, security
 * findings, drift detections, etc.), reads each one's
 * `affectedResourceKeys`, and produces a `Map<resourceId, severity>`
 * where the value is the max severity across all findings affecting
 * that resource. The scene builder reads the map and stamps
 * `attrs.findingSeverity` + `attrs.findingCount` on leaves so the
 * resource-card renderer can paint a coloured severity badge in the
 * top-left without re-walking the graph per node.
 *
 * Cost anomalies are deliberately *excluded*: they're already
 * surfaced via the per-resource cost lens badge. Doubling them up
 * with a severity dot adds noise instead of signal.
 */

import type { PersistedGraph, PersistedNode } from '../../models/KnowledgeGraph';

export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

const SEVERITY_ORDER: Record<FindingSeverity, number> = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

const SECURITY_FINDING_KINDS = new Set([
  'finding',
  'policy_violation',
  'coverage_gap',
  'attack_path',
  'compliance_finding',
  'observability_gap',
]);

function pickHigher(a: FindingSeverity | undefined, b: FindingSeverity): FindingSeverity {
  if (!a) return b;
  return SEVERITY_ORDER[b] > SEVERITY_ORDER[a] ? b : a;
}

function readSeverity(node: PersistedNode): FindingSeverity {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const raw = typeof props.severity === 'string' ? props.severity.toLowerCase() : '';
  if (raw === 'critical' || raw === 'high' || raw === 'medium' || raw === 'low' || raw === 'info') {
    return raw;
  }
  return 'medium';
}

export interface FindingAttribution {
  /** Highest severity finding affecting each resource. */
  severityById: Map<string, FindingSeverity>;
  /** Count of distinct findings affecting each resource. */
  countById: Map<string, number>;
}

/**
 * Build a per-resource severity / count map from the graph's finding
 * nodes. Cost anomalies are skipped — they live on the cost lens.
 */
export function attributeFindings(graph: PersistedGraph): FindingAttribution {
  const severityById = new Map<string, FindingSeverity>();
  const countById = new Map<string, number>();

  for (const node of graph.nodes) {
    const props = (node.props ?? {}) as Record<string, unknown>;
    const kind = typeof props.kind === 'string' ? props.kind : '';
    if (!SECURITY_FINDING_KINDS.has(kind)) continue;
    const affected = props.affectedResourceKeys;
    if (!Array.isArray(affected) || affected.length === 0) continue;
    const severity = readSeverity(node);
    for (const key of affected) {
      if (typeof key !== 'string' || !key) continue;
      severityById.set(key, pickHigher(severityById.get(key), severity));
      countById.set(key, (countById.get(key) ?? 0) + 1);
    }
  }

  return { severityById, countById };
}
